import React, {useState, useEffect, useMemo} from "react";
import { useNavigate } from "react-router-dom";
import { AuthService, EventsService, CustomerService, EmployeeService, ResourceService, trackActionLog, formatCustomerDisplayName } from "../../services";
import { buildExternalDriverOptions, formatDriverDropdownLabel, isDriverEligibleEmployee, mergeDriverOptionsById } from "../../shared/utils/driverDropdownOptions";
import DatePicker from "react-datepicker";
import { Spinner, Breadcrumb, BreadcrumbItem, Tabs, Tab, Button, Modal } from "react-bootstrap";
import { Columns, Download, Filter, PencilSquare, PersonSquare } from "react-bootstrap-icons";
import TimePicker from 'react-time-picker';
import 'react-time-picker/dist/TimePicker.css';
import { ManageTable } from "../../shared/components";
import { readPageUiObject, writePageUiObject } from "../../shared/pageFilterPersistence";
import { formatMedicalEventCustomerNameWithDischargeStatus } from "../../shared/constants/customer.constant";
import { formatMedicalProviderAddressDisplay, formatProviderContactDisplay, resolveEventProviderSpecialtyDisplay } from "../../shared/constants/resource.constant";
import { usePageScrollPinnedTableHeader } from "../../shared/hooks";

const resolveCustomerForOneDayList = (medicalEvent, customers) => {
  if (!medicalEvent || !customers?.length) return null;
  const id = medicalEvent?.data?.customer;
  if (id) {
    const c = customers.find((x) => String(x.id) === String(id));
    if (c) return c;
  }
  const label = medicalEvent?.data?.client_name;
  if (label) {
    return customers.find((c) => c.name === label) || null;
  }
  return null;
};

const resolveResourceForOneDayList = (medicalEvent, resources) => {
  const resourceId = medicalEvent?.data?.resource;
  return resourceId
    ? resources.find((r) => String(r.id) === String(resourceId))
    : null;
};

const resolveProviderContactForOneDayList = (medicalEvent, resources) => {
  return formatProviderContactDisplay(resolveResourceForOneDayList(medicalEvent, resources), medicalEvent?.data);
};

const resolveProviderSpecialtyForOneDayList = (medicalEvent, resources) => {
  return resolveEventProviderSpecialtyDisplay(resolveResourceForOneDayList(medicalEvent, resources), medicalEvent?.data);
};

/** "HH:MM" for react-time-picker from a Date or ISO string. */
const toTimePickerValue = (value) => {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** Match backend stripTrailingTimeFromTitle — driver part before trailing pick-up time in link_event_name. */
const driverNameFromLinkEventName = (linkName) => {
  if (!linkName || typeof linkName !== 'string') return null;
  const stripped = linkName
    .trim()
    .replace(/\s+\d{1,2}:\d{2}(?::\d{2})?(\s*[AP]M)?$/i, '')
    .trim();
  return stripped || null;
};

/** Parse trailing time in link_event_name (e.g. "Zahra, Mohamed 9:30:00 AM") so modal matches the cell, not the medical appointment. */
const parseTimeFromLinkEventName = (linkName) => {
  if (!linkName || typeof linkName !== 'string') return null;
  const m = String(linkName).match(/(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)\s*$/i);
  if (m) {
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    const ap = m[3].toUpperCase();
    if (Number.isNaN(h) || Number.isNaN(min)) return null;
    if (ap === 'PM' && h < 12) h += 12;
    if (ap === 'AM' && h === 12) h = 0;
    return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
  }
  return null;
};

const findDriverIdByName = (driverList, namePart) => {
  if (!namePart || !Array.isArray(driverList) || !driverList.length) return null;
  const t = namePart.trim().toLowerCase();
  if (!t) return null;
  const d = driverList.find((o) => (o?.name || '').trim().toLowerCase() === t);
  return d?.id != null && d.id !== '' ? String(d.id) : null;
};

const driverIdInOptions = (id, driverList) =>
  id != null &&
  id !== '' &&
  (driverList || []).some((o) => String(o.id) === String(id));

/** Driver display key for grouping (from link_event_name / transportation cell). Empty = unassigned. */
const oneDayListDriverGroupKey = (event) => {
  const fromLink = driverNameFromLinkEventName(event?.link_event_name || event?.transportation || '');
  return (fromLink || '').trim().toLowerCase();
};

/** Within a driver group: Language Support = Center OR Eyes-On = Yes go after time-sorted appointments. */
const isOneDayListDeferredInDriverGroup = (event) => {
  const language = `${event?.translation ?? event?.data?.interpreter ?? ''}`.trim().toLowerCase();
  if (language === 'center') return true;
  const eyesOn = `${event?.eyes_on ?? ''}`.trim().toLowerCase();
  return eyesOn === 'yes';
};

/**
 * One-Day List default order:
 * 1) Group by driver (same driver together; unassigned last)
 * 2) Within driver: Center language / Eyes-On Yes last (not interleaved by time with others)
 * 3) Remaining appointments: start time early → late
 */
const compareOneDayListDefaultOrder = (a, b) => {
  const driverA = oneDayListDriverGroupKey(a);
  const driverB = oneDayListDriverGroupKey(b);
  const aUnassigned = !driverA;
  const bUnassigned = !driverB;
  if (aUnassigned !== bUnassigned) {
    return aUnassigned ? 1 : -1;
  }
  if (driverA !== driverB) {
    return driverA.localeCompare(driverB);
  }

  const aDeferred = isOneDayListDeferredInDriverGroup(a);
  const bDeferred = isOneDayListDeferredInDriverGroup(b);
  if (aDeferred !== bDeferred) {
    return aDeferred ? 1 : -1;
  }

  const timeA = a?.start_time ? new Date(a.start_time).getTime() : 0;
  const timeB = b?.start_time ? new Date(b.start_time).getTime() : 0;
  if (timeA !== timeB) {
    return timeA - timeB;
  }

  const customerA = (a?.customer || '').toLowerCase();
  const customerB = (b?.customer || '').toLowerCase();
  return customerA.localeCompare(customerB);
};

const EventsList = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [resources, setResources] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  // Multi-column sorting: array of {key, order} objects
  // First item has highest priority, subsequent items are secondary sorts
  const [sortingRules, setSortingRules] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showTransportationModal, setShowTransportationModal] = useState(false);
  const [driver, setDriver] = useState(null);
  const [driverOptions, setDriverOptions] = useState([]);
  const [transportStartTime, setTransportStartTime] = useState(null);
  const [transportOptionsList, setTransportationOptionsList] = useState([]);
  const [transportSelected, setTransportSelected] = useState(null);
  const [showDeletedItems, setShowDeletedItems] = useState(false);
  const [medicalListExporting, setMedicalListExporting] = useState(false);
  const [columns, setColumns] = useState([
    {
      key: 'customer',
      label:'Customer Name',
      show: true
    },
    {
      key: 'member_type',
      label: 'Customer Type',
      show: true
    },
    {
      key: 'translation',
      label: 'Language Support',
      show: true
    },
    {
      key: 'transMethod',
      label: 'Transportation Support',
      show: true
    },
    {
      key: 'transportation',
      label: 'Driver',
      show: true
    },
    {
      key: 'startTime',
      label: 'Appointment Time',
      show: true
    },
    {
      key: 'eyes_on',
      label: 'Eyes-On',
      show: true
    },
    {
      key: 'newPatient',
      label: 'New Patient',
      show: true
    },
    {
      key: 'needId',
      label: 'ID Needed',
      show: true
    },
    {
      key: 'doctor',
      label: 'Provider',
      show: true
    },
    {
      key: 'specialty',
      label: 'Specialty',
      show: true
    },
    {
      key: 'phone',
      label: 'Provider Contact',
      show: true
    },
    {
      key: 'address',
      label: 'Provider Address',
      show: true
    },
    {
      key: 'fasting',
      label: 'Fasting Required',
      show: true
    },
    {
      key: 'lift_equipped_vehicle',
      label: 'Lift-Equipped Vehicle Required',
      show: true
    }
  ]);
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  useEffect(() => {
    const saved = readPageUiObject("events_one_day_list");
    if (saved) {
      if (typeof saved.showDeletedItems === "boolean") setShowDeletedItems(saved.showDeletedItems);
      if (Array.isArray(saved.sortingRules)) setSortingRules(saved.sortingRules);
    }
    setFiltersHydrated(true);
  }, []);

  const tableFiltersSnapshot = useMemo(
    () => ({
      showDeletedItems,
      sortingRules,
    }),
    [showDeletedItems, sortingRules]
  );
  useEffect(() => {
    if (!filtersHydrated) return;
    const t = setTimeout(() => {
      writePageUiObject("events_one_day_list", tableFiltersSnapshot);
    }, 400);
    return () => clearTimeout(t);
  }, [filtersHydrated, tableFiltersSnapshot]);

  const getCustomerEyesOnDisplay = (customers, event) => {
    const currentCustomer = customers?.find(
      (c) =>
        c?.id === event?.data?.customer ||
        c?.name === event?.data?.client_name ||
        c?.name === event?.target_name
    );
    if (currentCustomer) {
      const raw = currentCustomer.eyes_on;
      if (raw !== undefined && raw !== null && `${raw}`.trim() !== '') {
        const lowered = `${raw}`.trim().toLowerCase();
        if (lowered === 'yes') return 'Yes';
        if (lowered === 'no') return 'No';
      }
      return currentCustomer.disability ? 'Yes' : 'No';
    }
    return event?.data?.disability?.toLowerCase() === 'yes' ? 'Yes' : 'No';
  };

  const getLiftEquippedVehicleDisplay = (customers, event) => {
    const currentCustomer = resolveCustomerForOneDayList(event, customers);
    const raw = currentCustomer?.wheelchair;
    if (raw === undefined || raw === null) return '';
    const normalized = `${raw}`.trim();
    if (!normalized) return '';
    const lowered = normalized.toLowerCase();
    if (lowered === 'yes') return 'Yes';
    if (lowered === 'no') return 'No';
    return normalized;
  };
  // Default sort: driver groups, then time (with Center / Eyes-On Yes deferred within group)
  const applyDefaultSort = (eventsArray) => {
    return [...eventsArray].sort(compareOneDayListDefaultOrder);
  };

  useEffect(() => {
    if (!AuthService.canViewDriverAssignment()) {
      window.alert('You haven\'t login yet OR this user does not have access to this page. Please change an admin account to login.')
      AuthService.logout();
      navigate(`/login`);
    }
    CustomerService.getAllCustomers().then((data) => {
      setCustomers(data.data);
    })
    if (AuthService.canEditDriverAssignment()) {
      Promise.all([
        EmployeeService.getAllEmployees(),
        EmployeeService.getExternalUserPermissionsList(EventsService.site),
      ]).then(([employeeResponse, externalPermissionResponse]) => {
        const employees = employeeResponse?.data || [];
        const internalDrivers = employees.filter(isDriverEligibleEmployee);
        const externalDrivers = buildExternalDriverOptions(externalPermissionResponse?.data || []);
        setDriverOptions(mergeDriverOptionsById(internalDrivers, externalDrivers));
      });
    }
    ResourceService.getAll().then(data => {
      setResources(data.data);
    }) 
  }, []);

  useEffect(() => {
    if (customers?.length > 0 && resources?.length>0) {
      EventsService.getAllEvents({ date: EventsService.formatDate(selectedDate) }).then((data) => {
        const processedEvents = data.data.filter((item) => {
          item.customer = item?.data?.customer ? (customers.find(c=>c.id === item?.data?.customer)?.name || item?.data?.client_name || '') : (item?.data?.client_name || '');
          item.doctor = item?.data?.resource ? (resources.find(r=> r.id === item?.data?.resource)?.name || item?.data?.resource_name || '') : (item?.data?.resource_name || '');
          item.specialty = resolveProviderSpecialtyForOneDayList(item, resources);
          item.phone = resolveProviderContactForOneDayList(item, resources);
          item.address = formatMedicalProviderAddressDisplay(
            item?.data?.resource ? resources.find((r) => r.id === item?.data?.resource) : null,
            item?.data
          );
          item.translation = EventsService.getMedicalLanguageSupportDisplay(item?.data?.interpreter || '');
          item.newPatient = item?.data?.new_patient || '';
          item.needId = item?.data?.need_id || '';
          item.disability = item?.data?.disability || '';
          item.member_type = item?.data?.customer ? (customers.find(c => c.id === item?.data?.customer)?.type || '') : '';
          item.eyes_on = getCustomerEyesOnDisplay(customers, item);
          item.startTime = item?.start_time? `${new Date(item?.start_time).toLocaleDateString()} ${new Date(item?.start_time).toLocaleTimeString()}` : '' ;
          item.fasting = item?.data?.fasting || '';
          item.lift_equipped_vehicle = getLiftEquippedVehicleDisplay(customers, item);
          item.chinese_name = item?.data?.customer ? customers.find(c => c.id === item?.data?.customer)?.name_cn : (customers?.find(c=> c?.name === item?.data?.client_name || c?.name === item?.target_name )?.name_cn || '');
          item.transportation = item?.link_event_name || '';
          item.dob = item?.data?.customer ? customers.find(c => c.id === item?.data?.customer)?.birth_date : (item?.data?.client_birth_date || '');
          item.transMethod = EventsService.getMedicalTransportationSupportDisplay(item?.data?.trans_method);
          return item;
        }).filter(item => item.type === 'medical' && item.confirmed);
        
        const sortedEvents = applyMultiColumnSort(processedEvents, sortingRules);
        setEvents(sortedEvents);
        setTransportationOptionsList(data.data.filter((item) => item.type === 'transportation' && item.status === 'active'))
      })
    }
  }, [selectedDate, resources, customers]);



  // Apply multi-column sorting (empty rules → One-Day List default driver/time order)
  const applyMultiColumnSort = (eventsArray, rules) => {
    if (!rules?.length) {
      return applyDefaultSort(eventsArray);
    }
    
    return [...eventsArray].sort((a, b) => {
      // First apply all custom sorting rules
      for (const rule of rules) {
        const valA = (a[rule.key] || '').toString().toLowerCase();
        const valB = (b[rule.key] || '').toString().toLowerCase();
        const comparison = valA.localeCompare(valB);
        
        if (comparison !== 0) {
          return rule.order === 'desc' ? -comparison : comparison;
        }
      }
      
      // Stable fallback: same One-Day List default order
      return compareOneDayListDefaultOrder(a, b);
    });
  };

  useEffect(() => {
    if (events.length === 0) return;
    
    const sortedEvents = applyMultiColumnSort(events, sortingRules);
    setEvents(sortedEvents);
  }, [sortingRules]);


  const goToEdit = (id) => {
    if (!AuthService.canEditMedicalEvents()) return;
    navigate(`/medical/events/edit/${id}`)
  }

  const goToEventsCalendar = () => {
    navigate(`/medical/events/calendar`)
  }

  const goToMultipleList = () => {
    navigate(`/medical/events/multiple-list`)
  }

  const goToResourceList = () => {
    navigate(`/medical/resources/list`)
  }


  const goToView = (id) => {
    navigate(`/medical/events/${id}`)
  }

  const goToNextDay = () => {
    setSelectedDate(new Date(selectedDate.setDate(selectedDate.getDate() + 1)));
  }

  const goToPreviousDay = () => {
    setSelectedDate(new Date(selectedDate.setDate(selectedDate.getDate() - 1)));
  }

  const showDeleted = (value) => {
		setShowDeletedItems(value === 'archivedEvents');
		// Recover all filters
		// setKeyword('');
		setSortingRules([]);
		setSelectedItems([]);
	}

  // Get the sort indicator for a column (shows priority number if multi-column)
  const getSortingImg = (key) => {
    const ruleIndex = sortingRules.findIndex(rule => rule.key === key);
    if (ruleIndex === -1) {
      return 'default';
    }
    return sortingRules[ruleIndex].order === 'asc' ? 'up_arrow' : 'down_arrow';
  }

  // Multi-column sort: clicking a column adds it to sort rules or toggles its order
  // Behavior: asc → desc → remove from sorting
  const sortTableWithField = (key) => {
    const existingIndex = sortingRules.findIndex(rule => rule.key === key);
    
    if (existingIndex === -1) {
      // Column not in sort rules - add it with 'asc' order
      setSortingRules([...sortingRules, { key, order: 'asc' }]);
    } else {
      const existingRule = sortingRules[existingIndex];
      if (existingRule.order === 'asc') {
        // Toggle to desc
        const newRules = [...sortingRules];
        newRules[existingIndex] = { key, order: 'desc' };
        setSortingRules(newRules);
      } else {
        // Already desc - remove from sorting rules
        const newRules = sortingRules.filter((_, index) => index !== existingIndex);
        setSortingRules(newRules);
      }
    }
  }

  const toggleSelectedAllItems = () => {
    if (selectedItems.length !== events.length || selectedItems.length === 0) {
      const newSelectedItems = [...events].map((event) => event.id);
      setSelectedItems(newSelectedItems);
    } else {
      setSelectedItems([]);
    }
  }

  const toggleItem  = (id) => {
    if (selectedItems.includes(id)) {
      const newSelectedItems = [...selectedItems].filter((item) => item !== id);
      setSelectedItems(newSelectedItems);
    } else {
      const newSelectedItems = [...selectedItems, id];
      setSelectedItems(newSelectedItems);
    }
  }

  const checkSelectAll = () => {
    return selectedItems.length === events.length && selectedItems.length > 0;
  }

  const disableAssignTransportationButton  = () => {
    return (!transportSelected || transportSelected === '') || ((transportSelected === 'create_new') && (!driver || !transportStartTime || driver === '' || transportStartTime === ''));
  }

  const closePanel = () => {
    setShowTransportationModal(false);
    setTransportStartTime(null);
    setDriver(null);
    setTransportSelected(null);
  }

  /** Open panel from Driver column: time + driver match table (link_event_name) first, then linked transport, then appt. */
  const openAssignPanelFromEvent = (medicalEvent) => {
    if (!AuthService.canEditDriverAssignment() || !medicalEvent) return;
    setSelectedItems([medicalEvent.id]);
    setTransportSelected('create_new');
    const linkName = (medicalEvent.link_event_name || medicalEvent.transportation || '').trim();
    const linkId = medicalEvent.link_event_uuid;
    const trans = linkId
      ? transportOptionsList.find((t) => String(t.id) === String(linkId))
      : null;
    const parsedTimeFromLink = linkName ? parseTimeFromLinkEventName(linkName) : null;
    const parsedDriverName = linkName ? driverNameFromLinkEventName(linkName) : null;
    const driverByName = parsedDriverName ? findDriverIdByName(driverOptions, parsedDriverName) : null;

    let timeVal = toTimePickerValue(medicalEvent.start_time);
    if (trans?.start_time != null) {
      timeVal = toTimePickerValue(trans.start_time) ?? timeVal;
    }
    if (parsedTimeFromLink) {
      timeVal = parsedTimeFromLink;
    }

    let driverId = null;
    if (trans != null) {
      const tuuid = trans.target_uuid;
      if (tuuid != null && `${tuuid}`.trim() !== '') {
        const tid = String(tuuid);
        if (driverIdInOptions(tid, driverOptions)) {
          driverId = tid;
        }
      }
    }
    if (!driverId && driverByName) {
      driverId = driverByName;
    }
    setTransportStartTime(timeVal);
    setDriver(driverId);
    setShowTransportationModal(true);
  };

  const assignDriver = () => {
    // if select create new event, then create a new transportation event first
    if (transportSelected === 'create_new') {
      const timeStr = transportStartTime == null ? '' : String(transportStartTime);
      const parts = timeStr.includes(':') ? timeStr.split(':') : ['0', '0'];
      const hours = parseInt(parts[0], 10);
      const minutes = parseInt(parts[1], 10);
      const h = Number.isNaN(hours) ? 0 : hours;
      const m = Number.isNaN(minutes) ? 0 : minutes;
      const day = selectedDate || new Date();
      const startDateTime = new Date(
        day.getFullYear(),
        day.getMonth(),
        day.getDate(),
        h,
        m,
        0,
        0
      );
      const transportationParameter = {
        title: `${driverOptions.find((item) => item.id === driver)?.name} ${startDateTime.toLocaleTimeString()}`,
        description: 'transportatoin for med events',
        type: 'transportation',
        source_type: 'resource',
        source_uuid: '',
        target_type: 'staff',
        target_uuid: driver,
        start_time: startDateTime,
        stop_time: startDateTime,
        status: 'active',
        create_by: JSON.parse(localStorage.getItem('user'))?.name,
        create_date: new Date(),
        edit_by: JSON.parse(localStorage.getItem('user'))?.name,
        edit_date: new Date(),
        edit_history: [{
          employee: JSON.parse(localStorage.getItem('user'))?.name,
          date: new Date()
        }]
      };

      EventsService.createNewEvent(transportationParameter).then(data => {
        const trans = data.data;
        EventsService.assignTransportationToEvents({
          transportationId: trans.id,
          transportationName: trans.title,
          eventIds: selectedItems
        }).then(() => {
          setSelectedItems([]);
          EventsService.getAllEvents({ date: EventsService.formatDate(selectedDate) }).then((data) => {
            const results = [...data.data];
            const eventsResults = results.filter((item) => {
              item.customer = item?.data?.customer ? (customers.find(c=>c.id === item?.data?.customer)?.name || item?.data?.client_name || '') : (item?.data?.client_name || '');
              item.doctor = item?.data?.resource ? (resources.find(r=> r.id === item?.data?.resource)?.name || item?.data?.resource_name || '') : (item?.data?.resource_name || '');
              item.specialty = resolveProviderSpecialtyForOneDayList(item, resources);
              item.phone = resolveProviderContactForOneDayList(item, resources);
              item.address = formatMedicalProviderAddressDisplay(
            item?.data?.resource ? resources.find((r) => r.id === item?.data?.resource) : null,
            item?.data
          );
              item.translation = EventsService.getMedicalLanguageSupportDisplay(item?.data?.interpreter || '');
              item.newPatient = item?.data?.new_patient || '';
              item.needId = item?.data?.need_id || '';
              item.disability = item?.data?.disability || '';
              item.member_type = item?.data?.customer ? (customers.find(c => c.id === item?.data?.customer)?.type || '') : '';
              item.eyes_on = getCustomerEyesOnDisplay(customers, item);
              item.startTime = item?.start_time? `${new Date(item?.start_time).toLocaleDateString()} ${new Date(item?.start_time).toLocaleTimeString()}` : '' ;
              item.fasting = item?.data?.fasting || '';
              item.lift_equipped_vehicle = getLiftEquippedVehicleDisplay(customers, item);
              item.chinese_name = item?.data?.customer ? customers.find(c => c.id === item?.data?.customer)?.name_cn : (customers?.find(c=> c?.name === item?.data?.client_name || c?.name === item?.target_name )?.name_cn || '');
              item.transportation = item?.link_event_name || '';
              item.dob = item?.data?.customer ? customers.find(c => c.id === item?.data?.customer)?.birth_date : (item?.data?.client_birth_date || '');
              item.transMethod = EventsService.getMedicalTransportationSupportDisplay(item?.data?.trans_method);
              return item;
            }).filter(item => item.type === 'medical' && item.confirmed);
            
            // Apply current sorting rules (or default if none)
            const sortedEvents = applyMultiColumnSort(eventsResults, sortingRules);
            setEvents(sortedEvents);
            setTransportationOptionsList(data.data.filter((item) => item.type === 'transportation' && item.status === 'active'));
            closePanel();
          })
        });
      }).catch((err) => console.log('Transportation Event Creation failed'))
    } else {
      if (transportSelected && transportSelected !== '') {
        EventsService.assignTransportationToEvents({
          transportationId: transportSelected,
          transportationName: transportOptionsList.find((item) => item.id === transportSelected)?.title,
          eventIds: selectedItems
        }).then(() => {
          setSelectedItems([]);
          EventsService.getAllEvents({ date: EventsService.formatDate(selectedDate) }).then((data) => {
            const results = [...data.data];
            const eventsResults = results.filter((item) => {
              item.customer = item?.data?.customer ? (customers.find(c=>c.id === item?.data?.customer)?.name || item?.data?.client_name || '') : (item?.data?.client_name || '');
              item.doctor = item?.data?.resource ? (resources.find(r=> r.id === item?.data?.resource)?.name || item?.data?.resource_name || '') : (item?.data?.resource_name || '');
              item.specialty = resolveProviderSpecialtyForOneDayList(item, resources);
              item.phone = resolveProviderContactForOneDayList(item, resources);
              item.address = formatMedicalProviderAddressDisplay(
            item?.data?.resource ? resources.find((r) => r.id === item?.data?.resource) : null,
            item?.data
          );
              item.translation = EventsService.getMedicalLanguageSupportDisplay(item?.data?.interpreter || '');
              item.newPatient = item?.data?.new_patient || '';
              item.needId = item?.data?.need_id || '';
              item.disability = item?.data?.disability || '';
              item.member_type = item?.data?.customer ? (customers.find(c => c.id === item?.data?.customer)?.type || '') : '';
              item.eyes_on = getCustomerEyesOnDisplay(customers, item);
              item.startTime = item?.start_time? `${EventsService.formatDate(new Date(item?.start_time))} ${new Date(item?.start_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: 'numeric', hour12: true })}` : '' ;
              item.fasting = item?.data?.fasting || '';
              item.lift_equipped_vehicle = getLiftEquippedVehicleDisplay(customers, item);
              item.chinese_name = item?.data?.customer ? customers.find(c => c.id === item?.data?.customer)?.name_cn : (customers?.find(c=> c?.name === item?.data?.client_name || c?.name === item?.target_name )?.name_cn || '');
              item.transportation = item?.link_event_name || '';
              item.dob = item?.data?.customer ? customers.find(c => c.id === item?.data?.customer)?.birth_date : (item?.data?.client_birth_date || '');
              item.transMethod = EventsService.getMedicalTransportationSupportDisplay(item?.data?.trans_method);
              return item;
            }).filter(item => item.type === 'medical' && item.confirmed);
            
            // Apply current sorting rules (or default if none)
            const sortedEvents = applyMultiColumnSort(eventsResults, sortingRules);
            setEvents(sortedEvents);
            // setTransportationOptionsList(data.data.filter((item) => item.type === 'transportation' && item.status === 'active'));
            closePanel();
          })
        });
      }
    };
  };

  const canAssignTransport = AuthService.canEditDriverAssignment();
  const driverColumnInteractive = (statusParam) => canAssignTransport && statusParam === 'active';
  const visibleColumns = useMemo(() => columns.filter((col) => col.show), [columns]);
  const visibleRowCount = useMemo(
    () => (events || []).filter((event) => event.status === (showDeletedItems ? 'inactive' : 'active')).length,
    [events, showDeletedItems]
  );
  const tableWrapRef = usePageScrollPinnedTableHeader([
    visibleRowCount,
    visibleColumns,
    showDeletedItems,
    canAssignTransport,
  ]);

  const table = (statusParam) => {
    const isActiveTab =
      (showDeletedItems && statusParam === 'inactive') ||
      (!showDeletedItems && statusParam === 'active');
    return (
    <div className="list row mb-4">
    <div
      ref={isActiveTab ? tableWrapRef : undefined}
      className="col-md-12 events-one-day-table-wrap sticky-table-scroll sticky-table-scroll--appointment-one-day"
    >
      <table
        className={`personnel-info-table personnel-info-table--freeze-leading${
          canAssignTransport ? ' personnel-info-table--freeze-leading--with-checkbox' : ''
        }`}
      >
        <thead>
          <tr>
            {canAssignTransport && (
              <th className="th-checkbox"><input type="checkbox" checked={checkSelectAll()} onClick={() => toggleSelectedAllItems()}></input></th>
            )}
            <th className="th-index">No.</th>
            {
              visibleColumns.map((column, index) => (
                <th
                  className={`sortable-header${column.key === 'customer' ? ' th-freeze-customer-name' : ''}`}
                  key={index}
                >
                  {column.label}{' '}
                  <span className="float-right" onClick={() => sortTableWithField(column.key)} style={{ cursor: 'pointer' }}>
                    <img src={`/images/${getSortingImg(column.key)}.png`} alt="" />
                  </span>
                </th>
              ))
            }
          </tr>
        </thead>
        <tbody>
          {
            events && events.filter(event => event.status === statusParam).map((medicalEvent, index) => <tr key={medicalEvent.id}>
              {canAssignTransport && (
                <td className="td-checkbox"><input type="checkbox" checked={selectedItems.includes(medicalEvent.id)} onClick={()=>toggleItem(medicalEvent?.id)}/></td>
              )}
              <td className="td-index">{index + 1}</td>
              {visibleColumns.map((column) => (
                <td key={column.key} className={column.key === 'customer' ? 'td-freeze-customer-name' : undefined}>
                  {column.key === 'customer'
                    ? (() => {
                        const cust = resolveCustomerForOneDayList(medicalEvent, customers);
                        const base = cust
                          ? formatCustomerDisplayName(cust)
                          : `${medicalEvent?.customer || ''}${medicalEvent?.chinese_name ? ` (${medicalEvent.chinese_name})` : ''}`;
                        return formatMedicalEventCustomerNameWithDischargeStatus(base, cust);
                      })()
                    : column.key === 'transportation' && driverColumnInteractive(statusParam) ? (
                      (medicalEvent?.transportation || '').trim() !== '' ? (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-start"
                          onClick={() => openAssignPanelFromEvent(medicalEvent)}
                        >
                          {medicalEvent.transportation}
                        </button>
                      ) : (
                        ''
                      )
                    ) : (medicalEvent?.[column.key] || '')}
                </td>
              ))}
            </tr>)
          }
        </tbody>
      </table>
    </div>
  </div>
    );
  };

  const exportMedicalOneDayListFile = async () => {
    if (medicalListExporting) return;
    trackActionLog({
      actionVerb: "Export",
      summary: "Tap export button (medical one-day list)",
      entityType: "export",
      entityId: "medical-one-day-list",
    }).catch(() => {});
    setMedicalListExporting(true);
    try {
      const status = showDeletedItems ? "inactive" : "active";
      const response = await EventsService.exportMedicalOneDayList(
        EventsService.formatDate(selectedDate),
        status
      );
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", "Medical_Event_One_Day_List.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Medical one-day list export failed:", error);
      let message = "Failed to export medical one-day list.";
      try {
        const data = error?.response?.data;
        if (data instanceof Blob) {
          const text = await data.text();
          try {
            const parsed = JSON.parse(text);
            message = parsed.message || text;
          } catch {
            message = text || message;
          }
        } else if (error?.message) {
          message = error.message;
        }
      } catch {
        /* keep default */
      }
      window.alert(message);
    } finally {
      setMedicalListExporting(false);
    }
  };

  return (
    <>
      <div className="list row mb-4">
        <Breadcrumb>
          <Breadcrumb.Item href="/dashboard/dashboard">Medical</Breadcrumb.Item>
          <Breadcrumb.Item active>
            Appointment Information
          </Breadcrumb.Item>
        </Breadcrumb>
        <div className="col-md-12 text-primary">
           <h4>
              Medical Event One-Day List (With Driver Assignment)
           </h4> 
        </div>
      </div>
      <div className="app-main-content-list-container list-page events-one-day-list">
				<div className="app-main-content-list-func-container">
					<Tabs defaultActiveKey="activeEvents" id="requests-tab" onSelect={(k) => showDeleted(k)}>
						<Tab eventKey="activeEvents" title="Active Appointments">
              <div className="app-main-content-fields-section with-function">
                <Button className="me-2" variant="outline-primary" size="sm" onClick={() => goToPreviousDay()} > {'<'} </Button>
                <DatePicker className="me-2" popperClassName="medical-list-datepicker-popper" selected={selectedDate} onChange={(v) => setSelectedDate(v)} />
                <Button className="me-2 ms-2"  variant="outline-primary" size="sm" onClick={() => goToNextDay()}> {'>'} </Button>
                {AuthService.canEditDriverAssignment() && <Button className="me-2"  variant="primary" size="sm" disabled={selectedItems.length === 0} onClick={() => { setTransportStartTime(null); setDriver(null); setTransportSelected('create_new'); setShowTransportationModal(true); }}> + Show Assign Transportation Panel</Button>}
              </div>
							{table('active')}
						</Tab>
						<Tab eventKey="archivedEvents" title="Archived Appointments">
              <div className="app-main-content-fields-section with-function">
              <Button className="me-2" variant="outline-primary" size="sm" onClick={() => goToPreviousDay()} > {'<'} </Button>
                <DatePicker className="me-2" popperClassName="medical-list-datepicker-popper" selected={selectedDate} onChange={(v) => setSelectedDate(v)} />
                <Button className="me-2 ms-2"  variant="outline-primary" size="sm" onClick={() => goToNextDay()}> {'>'} </Button>
              </div>
							{table('inactive')}
						</Tab>
					</Tabs>
					<div className="list-func-panel">
						{/* <input className="me-2 with-search-icon" type="text" placeholder="Search" value={keyword}  onChange={(e) => setKeyword(e.currentTarget.value)} /> */}
						{/* <button className="btn btn-primary me-2"><Filter size={16} className="me-2"></Filter>Filter</button> */}
						<ManageTable columns={columns} onColumnsChange={setColumns} />
						{AuthService.canViewDriverAssignment() && (
							<button
								type="button"
								className="btn btn-primary me-2"
								disabled={medicalListExporting}
								onClick={() => void exportMedicalOneDayListFile()}
							>
								<Download size={16} className="me-2" />
								{medicalListExporting ? "Exporting…" : "Export"}
							</button>
						)}
					</div>
				</div>
      </div>
      <Modal show={showTransportationModal} onHide={() => closePanel()}>
        <Modal.Header closeButton>
          <Modal.Title>Assign Transportations Panel</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <>
            {transportSelected === 'create_new' && (
              <>
                <hr/>
                <div>
                  Start Time:
                  <div>
                    <TimePicker disableClock={true} value={transportStartTime} onChange={setTransportStartTime} />
                  </div>
                </div>
                
              </>)
            }

            {transportSelected === 'create_new' && (
              <>
                <hr/>
                <div>
                  Driver:
                  <div>
                    <select value={driver ?? ''} onChange={(e)=>{setDriver(e.currentTarget.value || null)}}>
                      <option value=""></option>
                      {
                        driverOptions.map((item) => <option key={item.id} value={item.id}>{formatDriverDropdownLabel(item)}</option>)
                      }
                    </select>
                  </div>
                </div>
              </>)
            }
          </>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => closePanel()}>
            Close
          </Button>
          <Button disabled={disableAssignTransportationButton()} variant="primary" onClick={() => assignDriver()}>
            Assign Transportation
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
};

export default EventsList;